/* eslint-disable react/prop-types */
import React from 'react';
import { useTheme } from 'styled-components';
import SkeletonBar from '../Skeleton';
import { tokens, orange } from '../tokens';

// Row of KPI tiles. Equal-width columns that fit as many tiles as the row
// allows, so a tile that wraps onto a second row stays the same size as the
// others instead of stretching across the page.
export const StatStrip = ({ children, minWidth = 190, style }) => (
  <div
    style={{
      display: 'grid',
      gridTemplateColumns: `repeat(auto-fit, minmax(min(${minWidth}px, 100%), 1fr))`,
      gap: 16,
      ...style,
    }}
  >
    {children}
  </div>
);

// Top-level KPI tile (design spec: card bg, border, radius 16, shadow-card).
// `accent` outlines the tile in orange and colours the value; `loading`
// swaps the value for a shimmer bar. `caption` (optional node) is a small
// line under the value, e.g. "+4 this week". Sized by its parent (see
// StatStrip).
//
// The tile is a size container so the value can scale with its width: a
// dollar figure like "$139,657.14" needs roughly 6.2em, so the font size is
// derived from the width inside the padding and capped at the 34px spec.
// That keeps the number inside the card when the nav sub-menu narrows the
// page and the tiles drop to their minimum width.
const VALUE_SIZE = 'clamp(20px, calc((100cqw - 40px) / 6.2), 34px)';

const StatTile = ({ label, value, caption, accent, loading = false }) => {
  const t = tokens(useTheme());
  return (
    <div
      style={{
        boxSizing: 'border-box',
        minWidth: 0,
        containerType: 'inline-size',
        background: t.card,
        border: '1px solid ' + (accent ? orange(0.32) : t.border),
        borderRadius: 16,
        boxShadow: t.shadowCard,
        padding: '18px 20px',
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 600, color: t.sub }}>{label}</div>
      {loading ? (
        <div style={{ marginTop: 12 }}>
          <SkeletonBar $w="55%" $h="30px" $r="8px" />
        </div>
      ) : (
        <div
          style={{
            fontSize: VALUE_SIZE,
            fontWeight: 800,
            letterSpacing: '-1px',
            lineHeight: 1.2,
            margin: '6px 0 0',
            color: accent ? t.primary : t.text,
            fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'nowrap',
          }}
        >
          {value}
        </div>
      )}
      {!loading && caption != null ? (
        <div
          style={{
            marginTop: 4,
            fontSize: 12.5,
            fontWeight: 600,
            color: t.sub,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {caption}
        </div>
      ) : null}
    </div>
  );
};

export default StatTile;
